import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import isEmpty from 'lodash-es/isEmpty';
import { observer } from 'mobx-react-lite';
import { useContext, useState } from 'react';
import * as shopService from 'services/shopService';
import StoreContext from 'stores/context/StoreContext';
import type { FC } from 'react';

interface MangoCredentialsDraft {
  apiKey: string;
  apiSalt: string;
}

/**
 * Маскирует строку, оставляя последние 4 символа
 *
 * @param value Secret
 * @returns Masked secret
 */
const maskSecret = (value: string): string =>
  isEmpty(value) ? 'Не задан' : `••••••••${value.slice(-4)}`;

/**
 * Компонент для настройки интеграции с Mango Office (vPBX).
 * Отображается только для Supervisor (право MangoIntegration).
 * Редактирование учётных данных происходит через диалог.
 */
const MangoIntegrationSettings: FC = observer(() => {
  const { shopStore, notifyStore } = useContext(StoreContext);
  const shop = shopStore.shop;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<MangoCredentialsDraft>({ apiKey: '', apiSalt: '' });
  const [isSaving, setIsSaving] = useState(false);

  const handleOpenDialog = (): void => {
    setDraft({
      apiKey: shop?.mangoVpbxApiKey ?? '',
      apiSalt: shop?.mangoVpbxApiSalt ?? '',
    });
    setDialogOpen(true);
  };

  const handleClose = (): void => {
    setDialogOpen(false);
  };

  /** Сохраняет учётные данные Mango vPBX */
  const handleSave = async (): Promise<void> => {
    if (shop == null) {
      return;
    }

    setIsSaving(true);
    try {
      await shopService.updateShopAsync(shop.id, {
        name: shop.name,
        description: shop.description,
        settings: shop.settings ?? {
          showOrdersWithoutActionDateInCalendar: false,
          enabledModules: {
            discounts: false,
            payouts: false,
            images: false,
            files: false,
          },
        },
        mangoVpbxApiKey: draft.apiKey || undefined,
        mangoVpbxApiSalt: draft.apiSalt || undefined,
      });

      shopStore.updateShopInfo();
      notifyStore.showNotify('Настройки Mango Office сохранены', 'success');
      setDialogOpen(false);
    } catch (error) {
      console.error('Ошибка при сохранении настроек Mango Office:', error);
      notifyStore.showNotify('Ошибка при сохранении настроек Mango Office', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant='h6'>Интеграция Mango Office</Typography>
      <Divider sx={{ my: 2 }} />

      <List>
        <ListItem>
          <ListItemIcon>
            <HeadsetMicIcon />
          </ListItemIcon>
          <ListItemText
            primary='Mango ВАТС'
            secondary={`Уникальный код АТС: ${maskSecret(
              shop?.mangoVpbxApiKey ?? ''
            )} · Ключ подписи: ${maskSecret(shop?.mangoVpbxApiSalt ?? '')}`}
          />
          <ListItemSecondaryAction>
            <Button variant='outlined' size='small' onClick={handleOpenDialog}>
              Изменить
            </Button>
          </ListItemSecondaryAction>
        </ListItem>
      </List>

      <SimpleDialog
        title='Настройки Mango Office'
        open={dialogOpen}
        onClose={handleClose}
        content={
          <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }}>
            <Alert severity='info'>
              Значения находятся в личном кабинете Mango Office:{' '}
              <strong>Настройки АТС → Интеграции → API коннектор</strong>.{' '}
              <Link
                href='https://www.mango-office.ru/support/integratsiya-api/'
                target='_blank'
                rel='noopener noreferrer'>
                Документация
              </Link>
            </Alert>
            <TextField
              autoFocus
              fullWidth
              label='Уникальный код АТС'
              value={draft.apiKey}
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, apiKey: e.target.value }));
              }}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              fullWidth
              label='Ключ для создания подписи'
              value={draft.apiSalt}
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, apiSalt: e.target.value }));
              }}
              inputProps={{ maxLength: 200 }}
            />
          </Stack>
        }
        actions={
          <>
            <LoadingButton
              loading={isSaving}
              variant='contained'
              onClick={() => {
                void handleSave();
              }}>
              Сохранить
            </LoadingButton>
            <Button onClick={handleClose} disabled={isSaving}>
              Отмена
            </Button>
          </>
        }
      />
    </Box>
  );
});

export default MangoIntegrationSettings;
